/**
 * Modal · Sync PLACSP (+ otras fuentes) con selector de rango + toggle "solo abiertas".
 * Se dispara desde MERCADO. Backend endpoint es async (HTTP 202).
 *
 * Al terminar emite `window` event `mercado-sync-completed` para que las tablas
 * abiertas se refresquen.
 */
import React, { useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Loader2, X, Globe, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Iter156 · Rangos en DÍAS (antes eran meses). Sólo se pueden descargar
// oportunidades recientes; nada de "6 meses hacia atrás" (bug reportado por
// David: el mínimo era 1 mes, muy largo para uso semanal).
const RANGE_OPTIONS = [
  { value: 1,   label: "Último 1 día",           hint: "más rápido" },
  { value: 2,   label: "Últimos 2 días",         hint: "revisión diaria + retraso" },
  { value: 7,   label: "Última semana",          hint: "recomendado — cadencia habitual" },
  { value: 30,  label: "Último mes",             hint: "cobertura amplia · más lento" },
];

export default function PlacspSyncModal({ open, onClose }) {
  const [daysBack, setDaysBack] = useState(7);
  const [onlyOpen, setOnlyOpen]     = useState(true);
  const [running, setRunning]       = useState(false);
  const [lastResult, setLastResult] = useState(null);

  if (!open) return null;

  const launch = async () => {
    setRunning(true);
    setLastResult(null);
    try {
      // Iter156 · Nuevo endpoint mercado/sync-now?days=N. Devuelve 202 accepted
      // y los nuevos registros llegan como EN_CARTERA, no se propagan
      // automáticamente al pipeline de Licitaciones.
      const r = await axios.post(
        `${API}/mercado/sync-now?days=${daysBack}`,
        {}
      );
      setLastResult({ ok: true, ...r.data });
      toast.success(r.data?.message || "Sync PLACSP iniciado en background", { duration: 6000 });
      window.dispatchEvent(new CustomEvent("mercado-sync-completed", { detail: { source: "placsp", days_back: daysBack, only_open: onlyOpen } }));
    } catch (e) {
      setLastResult({ ok: false, error: e.response?.data?.detail || e.message });
      toast.error("PLACSP sync: " + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="placsp-sync-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-6" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 px-5 py-3 flex items-center justify-between text-white rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <Globe size={20}/>
            <div>
              <h3 className="font-bold text-base">Sync PLACSP · MERCADO</h3>
              <p className="text-[11px] opacity-90">Descarga ATOM RSS del Estado + filtro CPV + Pando scoring</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="placsp-sync-close" disabled={running}>
            <X size={18}/>
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {/* Rango de meses */}
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-2">Rango de descarga</label>
            <div className="grid grid-cols-1 gap-1.5" data-testid="placsp-sync-range">
              {RANGE_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-center justify-between gap-3 border rounded-lg px-3 py-2 cursor-pointer transition ${daysBack === opt.value ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-indigo-300"}`}
                  data-testid={`placsp-sync-range-${opt.value}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="days_back"
                      value={opt.value}
                      checked={daysBack === opt.value}
                      onChange={() => setDaysBack(opt.value)}
                      className="accent-indigo-600"
                      disabled={running}
                    />
                    <span className={`text-xs ${daysBack === opt.value ? "font-bold text-indigo-900" : "text-gray-700"}`}>{opt.label}</span>
                  </div>
                  <span className="text-[10px] text-gray-500">{opt.hint}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Toggle solo abiertas */}
          <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 flex items-center gap-3">
            <input
              type="checkbox"
              id="only-open-toggle"
              checked={onlyOpen}
              onChange={e => setOnlyOpen(e.target.checked)}
              className="w-4 h-4 accent-emerald-600"
              disabled={running}
              data-testid="placsp-sync-only-open"
            />
            <label htmlFor="only-open-toggle" className="flex-1 cursor-pointer">
              <span className="text-xs font-semibold text-emerald-900">Solo licitaciones abiertas (PUB)</span>
              <p className="text-[10px] text-emerald-700 mt-0.5">Descarta expedientes ya resueltos/adjudicados/anulados/en evaluación — solo aparecen las que aún se pueden ofertar.</p>
            </label>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-[11px] text-blue-900">
            <b>📡 Proceso asíncrono:</b> el sync corre en background y puede tardar 1-5 minutos según el rango. Verás las nuevas oportunidades aquí y en Tender360/CuadroMando en unos minutos. Además se lanza <b>Pando scoring</b> sobre las nuevas y <b>alertas urgentes</b> a delegados regionales con score ≥ 90.
          </div>

          {/* Iter156 · Aviso alta controlada */}
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-[11px] text-amber-900" data-testid="placsp-sync-cartera-notice">
            <b>🛡️ Alta controlada:</b> las nuevas oportunidades entran como <b>EN CARTERA</b> (radar Mercado). No se dan de alta automáticamente en Licitaciones — sólo cuando tú aceptes una desde su ficha con el botón <b>"Aceptar → Crear licitación"</b>, indicando el estado destino (Publicada / Por decidir / En estudio).
          </div>

          {/* Resultado */}
          {lastResult && (
            <div className={`rounded-lg border p-3 ${lastResult.ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`} data-testid="placsp-sync-result">
              <div className="flex items-start gap-2">
                {lastResult.ok
                  ? <CheckCircle2 size={16} className="text-emerald-700 mt-0.5 shrink-0"/>
                  : <AlertTriangle size={16} className="text-rose-700 mt-0.5 shrink-0"/>}
                <div className="flex-1">
                  <p className={`font-bold text-sm ${lastResult.ok ? "text-emerald-900" : "text-rose-900"}`}>
                    {lastResult.ok ? "Sync lanzado correctamente" : "Error al lanzar sync"}
                  </p>
                  <p className={`text-[11px] mt-0.5 ${lastResult.ok ? "text-emerald-800" : "text-rose-800"}`}>
                    {lastResult.ok ? lastResult.message : lastResult.error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Botón lanzar */}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={running} className="text-xs border border-gray-200 px-3 py-2 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50" data-testid="placsp-sync-cancel">
              Cerrar
            </button>
            <button
              onClick={launch}
              disabled={running}
              className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              data-testid="placsp-sync-launch">
              {running ? <><Loader2 size={13} className="animate-spin"/> Lanzando…</> : <><Send size={13}/> Lanzar sync</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
